// Story 11.10 (ADO-1961): the Info Button (the designer's 3 Nov export, frame 00 and the M5 CSS
// export), the toggle that opens an Info Panel beside a label: first beside Margin.
//
// A toggletip, never a tooltip: a click or tap opens and closes it, never a hover (iPad, HHT).
// Its accessible name says what it explains and `aria-expanded` whether the panel is open; the
// caller owns the open state and the panel (`InfoPanel`).
//
// Inline beside a label (`InfoLabel`): positioned out of flow, vertically centred on the label
// and 4px after it, so it never changes the label row's height (M5: left 53px, top -4px against
// the 49px MARGIN label). 24 x 24 at both sizes; `touch` (tablet and phone) keeps the 24px
// visual and widens the tap area to 44px.

import type { ReactNode, Ref } from 'react';
import { Icon } from '../Icon/Icon';
import styles from './InfoButton.module.css';

export type InfoButtonSize = 'default' | 'touch';

export interface InfoButtonProps {
  /** The accessible name: what the panel explains ("How margin is worked out"). */
  readonly label: string;
  /** Whether the panel it opens is open (`aria-expanded`). */
  readonly expanded: boolean;
  readonly onClick: () => void;
  readonly size?: InfoButtonSize;
  /** The panel it opens, once open (`aria-controls`). */
  readonly controls?: string | undefined;
  readonly ref?: Ref<HTMLButtonElement>;
}

export function InfoButton({
  label,
  expanded,
  onClick,
  size = 'default',
  controls,
  ref,
}: InfoButtonProps) {
  return (
    <button
      ref={ref}
      type="button"
      className={styles.button}
      data-size={size}
      aria-label={label}
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={onClick}
    >
      <Icon name="question" size={16} />
    </button>
  );
}

/** The label an `InfoButton` sits beside: the positioning context the button hangs off. */
export function InfoLabel({ children }: { readonly children: ReactNode }) {
  return <span className={styles.label}>{children}</span>;
}
